// 主题动态加载器
import { getThemeConfig } from './index.js'
import request from '@/utils/request'

// 主题组件映射表（支持代码分割）
const themeComponentMap = {
  'default': {
    'Promo': () => import('./default/pages/Promo.vue'),
    'Gallery': () => import('./default/pages/Gallery.vue'),
    'WorkSubmit': () => import('./default/pages/WorkSubmit.vue'),
    'WorkDetail': () => import('./default/pages/WorkDetail.vue'),
    'UserCenter': () => import('./default/pages/UserCenter.vue'),
    'MyWorks': () => import('./default/pages/MyWorks.vue'),
    'PrintPreview': () => import('./common/PrintPreview.vue'),
    'Login': () => import('./default/pages/Login.vue'),
    'Register': () => import('./default/pages/Register.vue')
  },
  'zero-carbon': {
    'Promo': () => import('./zero-carbon/pages/Promo.vue'),
    'Gallery': () => import('./zero-carbon/pages/Gallery.vue'),
    'WorkSubmit': () => import('./zero-carbon/pages/WorkSubmit.vue'),
    'WorkDetail': () => import('./zero-carbon/pages/WorkDetail.vue'),
    'UserCenter': () => import('./zero-carbon/pages/UserCenter.vue'),
    'MyWorks': () => import('./zero-carbon/pages/MyWorks.vue'),
    'PrintPreview': () => import('./common/PrintPreview.vue'),
    'Login': () => import('./zero-carbon/pages/Login.vue'),
    'Register': () => import('./zero-carbon/pages/Register.vue')
  },
  'chem-video': {
    'Promo': () => import('./chem-video/pages/Promo.vue'),
    'Gallery': () => import('./chem-video/pages/Gallery.vue'),
    'WorkSubmit': () => import('./chem-video/pages/WorkSubmit.vue'),
    'WorkDetail': () => import('./chem-video/pages/WorkDetail.vue'),
    'UserCenter': () => import('./chem-video/pages/UserCenter.vue'),
    'MyWorks': () => import('./chem-video/pages/MyWorks.vue'),
    'PrintPreview': () => import('./common/PrintPreview.vue'),
    'Login': () => import('./chem-video/pages/Login.vue'),
    'Register': () => import('./chem-video/pages/Register.vue')
  },
  'kpzp': {
    'Promo': () => import('./kpzp/pages/Promo.vue'),
    'Gallery': () => import('./kpzp/pages/Gallery.vue'),
    'WorkSubmit': () => import('./kpzp/pages/WorkSubmit.vue'),
    'WorkDetail': () => import('./kpzp/pages/WorkDetail.vue'),
    'UserCenter': () => import('./kpzp/pages/UserCenter.vue'),
    'MyWorks': () => import('./kpzp/pages/MyWorks.vue'),
    'PrintPreview': () => import('./common/PrintPreview.vue'),
    'Login': () => import('./kpzp/pages/Login.vue'),
    'Register': () => import('./kpzp/pages/Register.vue')
  }
}

// 主题缓存
const themeCache = new Map()

// 获取主题配置（带缓存）
export async function loadTheme(themeCode) {
  // 检查缓存
  if (themeCache.has(themeCode)) {
    return themeCache.get(themeCode)
  }
  
  // 从后端API获取主题配置（动态，走新租户API）
  try {
    const result = await request({
      url: `/portal/theme/config/${themeCode}`,
      method: 'get'
    })

    if (result.code === 200 && result.data) {
      const backendConfig = result.data
      // 后端只返回租户信息，路由需要从本地配置合并
      const localConfig = getThemeConfig(themeCode)
      const merged = { ...backendConfig, code: backendConfig.tenantCode || themeCode }
      if (localConfig && localConfig.routes) {
        merged.routes = localConfig.routes
      }
      themeCache.set(themeCode, merged)
      return merged
    }
  } catch (error) {
    console.warn(`[Theme] 后端未找到主题 ${themeCode}，尝试使用本地配置`)
  }

  // 降级：使用本地配置（含 default 兜底）
  const localConfig = getThemeConfig(themeCode)
  if (localConfig) {
    themeCache.set(themeCode, localConfig)
    return localConfig
  }
  
  // 最后兜底：尝试加载 default 主题
  const defaultConfig = getThemeConfig('default')
  if (defaultConfig) {
    console.warn(`[Theme] 使用 default 主题兜底: ${themeCode}`)
    themeCache.set(themeCode, defaultConfig)
    return defaultConfig
  }
  
  throw new Error(`[Theme] 主题加载失败: ${themeCode}`)
}

// 动态加载主题组件（代码分割，未找到时降级到 default）
export function loadThemeComponent(themeCode, componentName) {
  const loaders = themeComponentMap[themeCode] || themeComponentMap['default']
  if (!loaders || !loaders[componentName]) {
    throw new Error(`[Theme] 未找到组件: ${themeCode}/${componentName}`)
  }
  return loaders[componentName]()
}

// 生成主题路由
export function generateThemeRoutes(themeConfig) {
  if (!themeConfig) {
    console.warn('[generateThemeRoutes] themeConfig is null')
    return []
  }
  
  // 兼容后端返回和本地配置的不同字段名
  const code = themeConfig.tenantCode || themeConfig.themeCode || themeConfig.code
  const routeBase = themeConfig.routeBase || ''
  const routes = themeConfig.routes || []
  
  console.log(`[generateThemeRoutes] Generating routes for ${code}, routeBase: ${routeBase}, routes count: ${routes.length}`)
  
  if (routes.length === 0) {
    console.warn(`[generateThemeRoutes] No routes defined for theme ${code}`)
    return []
  }
  
  return routes.map(route => {
    // 处理路径：如果 route.path 为空，直接使用 routeBase；否则拼接
    const fullPath = route.path 
      ? `${routeBase}/${route.path}`.replace(/\/+/g, '/')
      : routeBase
    
    console.log(`[generateThemeRoutes] Generated path: ${fullPath}`)
    
    return {
      path: fullPath,
      name: `${code}-${route.component}`,
      component: () => loadThemeComponent(code, route.component),
      meta: {
        ...route.meta,
        themeCode: code,
        auth: route.auth || false
      }
    }
  })
}

// 清除缓存
export function clearThemeCache() {
  themeCache.clear()
}
